import React, { useEffect, useState } from 'react';
import { CalendarClock, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { comunidadAdapter, type ComunidadCalendarEvent } from '@/hooks/api/comunidad';
import { usePsychologistContext } from '@/contexts/PsychologistContext';
import { BA_TIMEZONE } from '@/utils/timezone';
import { formacionBlockReason } from '@/pages/psychologist/tabs/comunidad/formacionBlockWindow';
import { isoToZoneParts } from './timeZoneHelpers';

/**
 * Bloqueos de agenda que se crean al anotarse a una formación de Comunidad (motivo
 * "Formación: <título> (N° edición)"). En las tres vistas de la Agenda se ven con su detalle:
 * título, hora y el link de Google Meet — en un tooltip al pasar el mouse y en un cartel al
 * hacer click. El link sale del evento de Comunidad (el bloqueo solo guarda el motivo).
 */

export const FORMACION_REASON_PREFIX = 'Formación:';

export const isFormacionReason = (reason?: string | null): reason is string =>
  !!reason && reason.startsWith(FORMACION_REASON_PREFIX);

export interface FormacionBlockData {
  /** Motivo tal como quedó en el bloqueo: "Formación: <título> (N° edición)". */
  reason: string;
  /** Inicio y fin del bloqueo, ISO (UTC). */
  startIso: string;
  endIso: string;
}

// ─── Eventos de Comunidad (para el link) — una sola consulta compartida ───────────────────

let eventsPromise: Promise<ComunidadCalendarEvent[]> | null = null;

function loadFormacionEvents(): Promise<ComunidadCalendarEvent[]> {
  if (!eventsPromise) {
    eventsPromise = comunidadAdapter
      .getContent()
      .then((c) => c.calendarEvents ?? [])
      .catch(() => {
        // No cachear el fallo: se reintenta la próxima vez que se pida.
        eventsPromise = null;
        return [] as ComunidadCalendarEvent[];
      });
  }
  return eventsPromise;
}

/** El evento de Comunidad que originó un bloqueo (o null mientras carga / si ya no existe). */
export function useFormacionEvent(reason?: string | null): ComunidadCalendarEvent | null {
  const [event, setEvent] = useState<ComunidadCalendarEvent | null>(null);
  useEffect(() => {
    if (!isFormacionReason(reason)) { setEvent(null); return; }
    let cancelled = false;
    loadFormacionEvents().then((events) => {
      if (cancelled) return;
      setEvent(events.find((ev) => ev.type === 'formacion' && formacionBlockReason(ev) === reason) ?? null);
    });
    return () => { cancelled = true; };
  }, [reason]);
  return event;
}

// ─── Formato ──────────────────────────────────────────────────────────────────────────────

/** "Jueves 15/10 · 11:00 a 12:00 hs", en el huso horario de la psicóloga. */
export function formatFormacionWhen(startIso: string, endIso: string, timezone: string): string {
  const from = isoToZoneParts(startIso, timezone);
  const to = isoToZoneParts(endIso, timezone);
  const [y, m, d] = from.date.split('-').map(Number);
  const weekday = new Intl.DateTimeFormat('es-AR', { weekday: 'long' }).format(new Date(y, (m || 1) - 1, d || 1));
  const cap = weekday.charAt(0).toUpperCase() + weekday.slice(1);
  return `${cap} ${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')} · ${from.time} a ${to.time} hs`;
}

function useViewerTimezone(): string {
  const { psychologist } = usePsychologistContext();
  return psychologist?.timezone || BA_TIMEZONE;
}

// ─── Detalle (lo que muestran el tooltip y el cartel) ────────────────────────────────────

export const FormacionBlockDetails: React.FC<{ data: FormacionBlockData }> = ({ data }) => {
  const tz = useViewerTimezone();
  const event = useFormacionEvent(data.reason);
  const meetUrl = event?.meetUrl ?? null;
  return (
    <div data-testid="formacion-block-details" className="flex flex-col gap-1.5 text-left">
      <p className="m-0 text-[13px] font-bold leading-snug text-[#2D2049]">{data.reason}</p>
      <p className="m-0 flex items-center gap-1.5 text-xs text-[#4A3D73]">
        <CalendarClock size={13} className="shrink-0" aria-hidden="true" />
        {formatFormacionWhen(data.startIso, data.endIso, tz)}
      </p>
      {meetUrl ? (
        <a
          href={meetUrl}
          target="_blank"
          rel="noreferrer"
          data-testid="formacion-block-meet-link"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-start gap-1.5 break-all text-xs font-semibold text-[#6D57BC] underline"
        >
          <ExternalLink size={13} className="mt-0.5 shrink-0" aria-hidden="true" />
          {meetUrl}
        </a>
      ) : (
        <p className="m-0 text-xs text-gray-500">El link de Google Meet todavía no está cargado.</p>
      )}
    </div>
  );
};

/** Tooltip al pasar el mouse. El contenido es "hoverable": se puede llegar al link y clickearlo. */
export const FormacionBlockHover: React.FC<{ data: FormacionBlockData; children: React.ReactElement }> = ({ data, children }) => (
  <Tooltip delayDuration={120}>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    <TooltipContent
      side="top"
      align="center"
      className="max-w-[320px] bg-white p-3 pointer-events-auto"
      onClick={(e) => e.stopPropagation()}
    >
      <FormacionBlockDetails data={data} />
    </TooltipContent>
  </Tooltip>
);

/** Cartel con el mismo detalle, al hacer click en el bloqueo. */
export const FormacionBlockDialog: React.FC<{ data: FormacionBlockData | null; onClose: () => void }> = ({ data, onClose }) => (
  <Dialog open={data !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
    <DialogContent className="flex flex-col gap-4 p-6">
      <DialogHeader className="mb-0">
        <DialogTitle className="m-0 text-[#2D2049]">Formación en tu agenda</DialogTitle>
      </DialogHeader>
      {data && <FormacionBlockDetails data={data} />}
      <Button className="self-end bg-[#665090] text-white hover:bg-[#665090]/90" onClick={onClose}>
        Cerrar
      </Button>
    </DialogContent>
  </Dialog>
);
